import { Grid, Box, FormControl } from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";

import { memo, useEffect, useRef, useState } from "react";

import { IDateFormControlProperty } from "@definitions/index";
import { FieldError } from "@components/FieldError";
import { flexCenterProps, paddingX1Props } from "@styles/global-css-class";
import { colors } from "@components/ThemeRegistry/theme";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";

function NewDatePickerFormControl({
	childKey,
	label,
	name,
	value,
	defaultValue,
	onChange,
	isRequired,
	isDisabled,
	isHidden,
	formState,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel = 6,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	zIndex,
	isExpanded,
	formatDate,
	shouldDisableDate,
	shouldDisableMonth,
	shouldDisableYear,
	datePickerViews,
	minDate,
	maxDate,
	errorName,
	isReturnId,
	id,
	openDate,
	showIndicator,
	isIndicatorTakeSpace,
	...props
}: Readonly<IDateFormControlProperty>) {
	const [dateValue, setDateValue] = useState<Dayjs | null>(null);
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [cleared, setCleared] = useState<boolean>(false);

	const handleValue = (dateValue: Dayjs | null) => {
		if (dateValue !== null && !dateValue.toString().startsWith('0001-01-01')) {
			return dateValue;
		}

		return null;
	};

	useEffect(() => {
		if (value !== null && value !== undefined && !value.toString().startsWith('0001-01-01')) {
			setDateValue(dayjs(value));
		} else {
			setDateValue(null);
		}
	}, [value]);

	useEffect(() => {
		if (cleared) {
			const timeout = setTimeout(() => {
				setCleared(false);
			}, 300);

			return () => clearTimeout(timeout);
		}
		return () => { };
	}, [cleared]);

	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded, inputRef.current])

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start',
		mt: -0.9,
	}}>
		<FormControl
			fullWidth
			margin='normal'
			required={isRequired}
			error={formState?.errors[errorName ?? name] !== undefined}
			{...props}
		>
			<Grid container xs={12}  flexWrap={'nowrap'}>
				<Grid item xs={isIndicatorTakeSpace?11:12}>
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
					>
						<DemoContainer
							components={["DatePicker"]}
							sx={{
								width: "100%",
							}}
						>
							<Box
								onFocus={() => setIsFocused(true)}
								onBlur={() => setIsFocused(false)}
								sx={{ width: "100%" }}
								key={`${childKey}-form-control-box`}
							>
								<DatePicker
									name={name}
									label={label}
									format={formatDate}
									views={datePickerViews}
									value={handleValue(dateValue)}
									referenceDate={openDate}
									onChange={(newValue) => {
										if (isReturnId) {
											if (newValue?.isValid()) {
												onChange({ value: newValue.format("YYYY-MM-DD"), id: id });
											}
											else {
												onChange({ value: newValue?.toString(), id: id });
											}
										}
										else {
											if (newValue?.isValid()) {
												onChange(newValue.format("YYYY-MM-DD"));
											}
											else {
												onChange(newValue?.toString());
											}
										}
									}}
									shouldDisableMonth={shouldDisableMonth}
									shouldDisableYear={shouldDisableYear}
									shouldDisableDate={shouldDisableDate}
									minDate={minDate}
									maxDate={maxDate}
									slotProps={{
										field: { clearable: true, onClear: () => setCleared(true) },
										textField: {
											required: isRequired,
											inputRef: inputRef,
											error: formState?.errors[errorName ?? name] !== undefined,
											sx: {
												backgroundColor: colors.white,
												width: "100%",
											}
										},
										popper: {
											sx: {
												zIndex: zIndex ?? 99999,
											}
										},
									}}
									disabled={isDisabled}
									sx={{
										width: "100%",
									}}
								/>
							</Box>
						</DemoContainer>
					</LocalizationProvider>
					{formState && <FieldError formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
				</Grid>
				{showIndicator !== undefined && showIndicator() == true &&
					<Grid item xs={isIndicatorTakeSpace?1:0} sx={{ ...flexCenterProps }}>
						<DiffInheritanceIndicator />
					</Grid>
				}
			</Grid>
			
		</FormControl>
	</Grid >;
};

export default memo(NewDatePickerFormControl);
